/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { EventChannel } from 'redux-saga';
import { put } from 'redux-saga/effects';

import { gameLogger } from '~/app';
import { GameMode } from '~/game';
import { GameState, GameType, isFreeGameType } from '~/server/src/dtos';
import { Controller, View } from '~/server/src/interfaces';
import Turfmaster from '~/server/src/tmmodel';

import { SetupChannelEvent } from '../channelEvent';
import { actions as setupActions } from '../state';
import { takeUntil } from '../utils/saga';

/**
 * Probe-only function to check if a rejoinable game exists.
 * Only sends RESTOREGAME to get metadata, does NOT perform full rejoin or send gameplay commands.
 * This is safe to call from start/menu screens without triggering game state changes.
 */
function* probeRejoinableGame(
  controller: Controller,
  view: View,
  channel: EventChannel<SetupChannelEvent>,
) {
  controller.receiveMsg(Turfmaster.stateMachine.RESTOREGAME, { view });

  const { state, params } = yield* takeUntil(channel, (event) => {
    return event.state === Turfmaster.stateMachine.RESTOREGAME;
  });

  if (state === Turfmaster.stateMachine.RESTOREGAME) {
    gameLogger.debug({ state, params }, 'controller => client: RESTOREGAME (probe)');
    const rejoinableGameState: GameState | undefined = params?.game;

    if (rejoinableGameState) {
      const mode: GameMode = (() => {
        if (isFreeGameType(rejoinableGameState.gameType)) {
          return 'free';
        } else if (
          rejoinableGameState.gameType === GameType.TournamentBronze ||
          rejoinableGameState.gameType === GameType.TournamentSilver ||
          rejoinableGameState.gameType === GameType.TournamentGold
        ) {
          return 'tournament';
        } else return 'training';
      })();

      // Only set rejoin state for button visibility/navigation - no full game restore
      yield put(setupActions.setRejoinState({ type: 'success', mode }));

      gameLogger.debug(
        { mode, gameId: rejoinableGameState.gameId },
        'probeRejoinableGame: Found rejoinable game',
      );
      return true;
    } else {
      gameLogger.debug('probeRejoinableGame: No rejoinable game found');
    }
  } else {
    gameLogger.warn({ state, params }, 'controller => client: UNKNOWN RESPONSE TO RESTOREGAME');
  }

  return false;
}

/**
 * Saga to probe for rejoinable games from start/menu screens.
 * This is a metadata-only check - it does NOT perform full game restore or send gameplay commands.
 * Full restore happens later via restoreGame saga when user navigates to game route.
 */
function* sagaRejoinGame(
  view: View,
  localController: Controller,
  remoteController: Controller,
  channel: EventChannel<SetupChannelEvent>,
) {
  if (!view || !localController || !remoteController || !channel) return false;

  yield put(setupActions.setRejoinState({ type: 'loading' }));

  // Try remote first, fallback to local
  const remoteProbeSuccessful = yield probeRejoinableGame(remoteController, view, channel);
  if (!remoteProbeSuccessful) {
    const localProbeSuccessful = yield probeRejoinableGame(localController, view, channel);

    if (!localProbeSuccessful) {
      yield put(setupActions.setRejoinState({ type: 'error' }));
      return false;
    }
  }

  return true;
}

export default sagaRejoinGame;
